/** Draft for public /try flow: settings in localStorage, IDF blob in IndexedDB. */

const META_KEY = 'public-analyze-draft-meta';
const IDB_NAME = 'public-analyze-draft';
const IDB_STORE = 'idf';
const IDB_KEY = 'public-analyze-idf';
export const PUBLIC_ANALYZE_MAX_IDF_BYTES = 25 * 1024 * 1024;

export type PublicAnalyzePendingAction = 'createAndRun' | null;

export type PublicAnalyzeDraftMeta = {
  type: string;
  climate: string;
  terrain: string;
  single_block_per_floor: boolean;
  skip_overwrite: boolean;
  pendingAction: PublicAnalyzePendingAction;
  version: '4.x.x';
  idfFileName?: string;
  analyzeSummary?: {
    hardMismatches?: number;
    mismatches?: number;
    timeImpact?: Record<string, number>;
  } | null;
  updatedAt: number;
};

export type PublicAnalyzeIdfRecord = {
  name: string;
  blob: Blob;
};

const openDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('IndexedDB open failed'));
  });

const idbPut = async (value: PublicAnalyzeIdfRecord): Promise<void> => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).put(value, IDB_KEY);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error('IndexedDB put failed'));
    };
  });
};

const idbGet = async (): Promise<PublicAnalyzeIdfRecord | null> => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readonly');
    const req = tx.objectStore(IDB_STORE).get(IDB_KEY);
    req.onsuccess = () => {
      db.close();
      resolve((req.result as PublicAnalyzeIdfRecord) || null);
    };
    req.onerror = () => {
      db.close();
      reject(req.error || new Error('IndexedDB get failed'));
    };
  });
};

const idbDelete = async (): Promise<void> => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).delete(IDB_KEY);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error('IndexedDB delete failed'));
    };
  });
};

export const saveMeta = (meta: Omit<PublicAnalyzeDraftMeta, 'updatedAt'> & { updatedAt?: number }) => {
  const payload: PublicAnalyzeDraftMeta = {
    ...meta,
    version: '4.x.x',
    updatedAt: meta.updatedAt ?? Date.now(),
  };
  localStorage.setItem(META_KEY, JSON.stringify(payload));
  return payload;
};

export const loadMeta = (): PublicAnalyzeDraftMeta | null => {
  try {
    const raw = localStorage.getItem(META_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PublicAnalyzeDraftMeta;
  } catch {
    return null;
  }
};

export const saveIdf = async (file: File): Promise<void> => {
  if (file.size > PUBLIC_ANALYZE_MAX_IDF_BYTES) {
    throw new Error('PUBLIC_ANALYZE_IDF_TOO_LARGE');
  }
  if (!/\.idf$/i.test(file.name)) {
    throw new Error('PUBLIC_ANALYZE_IDF_INVALID');
  }
  await idbPut({ name: file.name, blob: file });
  const meta = loadMeta();
  if (meta) {
    saveMeta({ ...meta, idfFileName: file.name });
  }
};

export const loadIdf = async (): Promise<File | null> => {
  const record = await idbGet();
  if (!record?.blob) return null;
  return new File([record.blob], record.name || 'model.idf', {
    type: 'application/octet-stream',
  });
};

export const loadDraft = async (): Promise<{
  meta: PublicAnalyzeDraftMeta | null;
  file: File | null;
}> => {
  const meta = loadMeta();
  const file = await loadIdf();
  return { meta, file };
};

/** Drops the stored IDF only. Settings in localStorage stay. */
export const clearIdf = async (): Promise<void> => {
  try {
    await idbDelete();
  } catch {
    // ignore missing DB
  }
  const meta = loadMeta();
  if (meta) {
    saveMeta({ ...meta, idfFileName: undefined });
  }
};

/** Removes localStorage meta and IndexedDB IDF. Safe to call multiple times. */
export const clearDraft = async (): Promise<void> => {
  localStorage.removeItem(META_KEY);
  try {
    await idbDelete();
  } catch {
    // ignore missing DB
  }
};

export const hasPendingCreateAndRun = (): boolean => {
  const meta = loadMeta();
  return meta?.pendingAction === 'createAndRun';
};
